import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ShoppingCart, 
  TrendingUp, 
  FileText, 
  Target,
  Users,
  Euro,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Zap,
  Plus,
  Eye,
  Send
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardVente = () => {
  const [stats, setStats] = useState(null);
  const [recentClients, setRecentClients] = useState([]);
  const [recentDevis, setRecentDevis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, clientsResponse, devisResponse] = await Promise.all([
        axios.get(`${API}/dashboard/vente`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/devis`)
      ]);

      setStats(statsResponse.data);
      setRecentClients(clientsResponse.data.slice(0, 5));
      setRecentDevis(devisResponse.data.slice(0, 5));
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard vente:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      prospect: { label: 'Prospect', class: 'badge-info' },
      devis: { label: 'Devis', class: 'badge-warning' },
      client: { label: 'Client', class: 'badge-success' },
      installe: { label: 'Installé', class: 'badge-success' },
      brouillon: { label: 'Brouillon', class: 'badge-secondary' },
      envoye: { label: 'Envoyé', class: 'badge-warning' },
      accepte: { label: 'Accepté', class: 'badge-success' },
      refuse: { label: 'Refusé', class: 'badge-danger' }
    };
    return badges[status] || { label: status, class: 'badge-secondary' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            Dashboard Vente
          </h1>
          <p className="text-gray-600">
            Pilotage commercial et suivi des performances de vente
          </p>
        </div>
        {/* Boutons supprimés selon demande utilisateur */}
      </div>

      {/* Stats Cards Vente */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="stats-card slide-up bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-blue-600">{stats?.total_prospects || 0}</div>
              <div className="stats-label text-blue-800">Total Prospects</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-orange-50 to-red-50 border-orange-200" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-orange-600">{stats?.prospects_chauds || 0}</div>
              <div className="stats-label text-orange-800">Prospects Chauds</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-yellow-600">{stats?.devis_envoyes || 0}</div>
              <div className="stats-label text-yellow-800">Devis Envoyés</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Objectifs & Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Objectifs Vente</h2>
              </div>
              <span className="text-sm text-gray-500">Mensuel</span>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">CA Prévisionnel</span>
                  <span className="text-lg font-bold text-primary">
                    {new Intl.NumberFormat('fr-FR', { 
                      style: 'currency', 
                      currency: 'EUR',
                      maximumFractionDigits: 0
                    }).format(stats?.ca_previsionnel || 0)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-primary to-primary-dark h-3 rounded-full transition-all duration-1000" 
                    style={{ width: `${Math.min((stats?.ca_previsionnel || 0) / (stats?.objectif_mensuel || 1) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0€</span>
                  <span>Objectif: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(stats?.objectif_mensuel || 0)}</span>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Taux de Transformation</span>
                  <span className="text-lg font-bold text-success">{stats?.taux_transformation?.toFixed(1) || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-success to-green-600 h-3 rounded-full transition-all duration-1000" 
                    style={{ width: `${Math.min(stats?.taux_transformation || 0, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <Euro className="w-5 h-5 text-secondary" />
              <h2 className="text-lg font-semibold text-gray-900">Performance Commerciale</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {Math.round((stats?.ca_previsionnel || 0) / (stats?.total_prospects || 1))}€
                </div>
                <div className="text-sm text-blue-800">Panier moyen</div>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {stats?.devis_envoyes || 0}
                </div>
                <div className="text-sm text-green-800">Devis actifs</div>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {Math.round((stats?.prospects_chauds || 0) / (stats?.total_prospects || 1) * 100)}%
                </div>
                <div className="text-sm text-purple-800">Prospects chauds</div>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border border-orange-200">
                <div className="text-2xl font-bold text-orange-600 mb-1">
                  15j
                </div>
                <div className="text-sm text-orange-800">Cycle moyen</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prospects Chauds */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-orange-500" />
                <h2 className="text-lg font-semibold text-gray-900">Prospects Chauds</h2>
              </div>
              <button className="text-sm text-primary hover:text-primary-dark font-medium">
                Voir tout
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {recentClients.filter(c => c.status === 'prospect').length > 0 ? (
              <div className="space-y-0">
                {recentClients.filter(c => c.status === 'prospect').slice(0, 4).map((client, index) => (
                  <div key={client.id} className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {client.prenom} {client.nom}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {client.ville}
                            </span>
                            <span className="flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              {client.consommation_annuelle || 'N/A'} kWh
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="btn btn-secondary btn-sm">
                          <Phone className="w-3 h-3" />
                        </button>
                        <button className="btn btn-primary btn-sm">
                          <FileText className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucun prospect chaud</p>
              </div>
            )}
          </div>
        </div>

        {/* Devis en Attente */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-yellow-500" />
                <h2 className="text-lg font-semibold text-gray-900">Devis en Attente</h2>
              </div>
              <button className="text-sm text-primary hover:text-primary-dark font-medium">
                Voir tout
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {recentDevis.filter(d => d.status === 'envoye').length > 0 ? (
              <div className="space-y-0">
                {recentDevis.filter(d => d.status === 'envoye').slice(0, 4).map((devis, index) => (
                  <div key={devis.id} className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center">
                          <FileText className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {devis.numero}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              {devis.puissance_kwc} kWc
                            </span>
                            <span className="flex items-center gap-1">
                              <Euro className="w-3 h-3" />
                              {new Intl.NumberFormat('fr-FR').format(devis.prix_ttc)}€
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="btn btn-secondary btn-sm">
                          <Eye className="w-3 h-3" />
                        </button>
                        <button className="btn btn-primary btn-sm">
                          <Send className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucun devis en attente</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardVente;